// features/auth/VerifyEmail.js - Email verification component
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaTimesCircle, FaEnvelope } from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import LoadingOverlay from '../../components/LoadingOverlay';

const VerifyEmail = () => {
  const { t } = useTranslation();
  const { verifyEmail, resendVerificationEmail, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get token from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const email = queryParams.get('email');
  
  const [verificationStatus, setVerificationStatus] = useState({
    isVerified: false,
    isError: false,
    message: '',
    errorType: '' // 'expired', 'invalid', or 'server'
  });
  
  const [resendStatus, setResendStatus] = useState({
    sent: false,
    error: false,
    message: ''
  });
  
  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setVerificationStatus({
          isVerified: false,
          isError: true,
          message: t('auth.invalidVerificationLink'),
          errorType: 'invalid'
        });
        return;
      }
      
      // Prevent multiple verification attempts
      if (loading || verificationStatus.isVerified || verificationStatus.isError) {
        return;
      }
      
      try {
        await verifyEmail(token);
        setVerificationStatus({
          isVerified: true,
          isError: false,
          message: t('auth.emailVerificationSuccess'),
          errorType: ''
        });
      } catch (err) {
        // Determine error type based on error message
        let errorType = 'server';
        if (err.message && err.message.includes('expired')) {
          errorType = 'expired';
        } else if (err.message && err.message.includes('invalid')) {
          errorType = 'invalid';
        }
        
        setVerificationStatus({
          isVerified: false,
          isError: true,
          message: err.message || t('auth.emailVerificationError'),
          errorType
        });
      }
    };
    
    verify();
  }, [token, verifyEmail, t, loading, verificationStatus.isVerified, verificationStatus.isError]);
  
  const handleResendVerification = async () => {
    setResendStatus({
      sent: false,
      error: false,
      message: ''
    });
    
    try {
      await resendVerificationEmail(email);
      setResendStatus({
        sent: true,
        error: false,
        message: t('auth.verificationEmailResent')
      });
    } catch (err) {
      setResendStatus({
        sent: false,
        error: true,
        message: err.message || t('auth.resendVerificationError')
      });
    }
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 260, 
        damping: 20 
      } 
    }
  };
  
  const iconVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 260, 
        damping: 20,
        delay: 0.3
      } 
    }
  };
  
  return (
    <div className="flex justify-center items-center min-h-[80vh] p-4">
      {loading && <LoadingOverlay />}
      
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full max-w-md"
      >
        <Card variant="glass" hover="glow" className="w-full">
          <CardHeader>
            <motion.div 
              className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto ${
                verificationStatus.isVerified 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                  : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              }`}
              variants={iconVariants}
            >
              {verificationStatus.isVerified ? (
                <FaCheckCircle className="text-4xl" />
              ) : (
                <FaTimesCircle className="text-4xl" />
              )}
            </motion.div>
            <CardTitle gradient={true} className="text-center">
              {verificationStatus.isVerified 
                ? t('auth.emailVerified') 
                : t('auth.emailVerificationFailed')}
            </CardTitle>
          </CardHeader>
          
          <CardContent>
            <motion.div 
              variants={itemVariants}
              className={`text-center p-4 rounded-md mb-4 ${
                verificationStatus.isVerified 
                  ? 'bg-green-50 dark:bg-green-900/10 text-green-800 dark:text-green-300' 
                  : 'bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-300'
              }`}
            >
              {verificationStatus.message}
            </motion.div>
            
            {/* Show resend option for expired tokens */}
            {verificationStatus.errorType === 'expired' && (
              <motion.div variants={itemVariants} className="mt-4">
                <p className="text-center mb-2">{t('auth.verificationLinkExpired')}</p>
                <Button
                  variant="secondary"
                  className="w-full flex items-center justify-center"
                  onClick={handleResendVerification}
                  disabled={loading || resendStatus.sent}
                >
                  <FaEnvelope className="mr-2" />
                  {t('auth.resendVerificationEmail')}
                </Button>
                
                {resendStatus.sent && (
                  <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/10 text-green-800 dark:text-green-300 rounded text-sm text-center">
                    {resendStatus.message}
                  </div>
                )}
                
                {resendStatus.error && (
                  <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-300 rounded text-sm text-center">
                    {resendStatus.message}
                  </div>
                )}
              </motion.div>
            )}
          </CardContent>
          
          <CardFooter className="flex justify-center">
            <motion.div variants={itemVariants}>
              <Button
                variant={verificationStatus.isVerified ? 'primary' : 'secondary'}
                animation="shimmer"
                onClick={() => navigate('/login')}
              >
                {t('auth.backToLogin')}
              </Button>
            </motion.div>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
